/**
 * The formula block's symbol keyboard.
 *
 * A formula is notation, not prose, and a creator writing one is doing three
 * things a phone keyboard cannot do: entering symbols that have no key (∫, λ,
 * ∠), writing *structure* (a fraction, an integral with limits, a root), and
 * doing it without leaving the block they are editing.
 *
 * So the keyboard is data, not UI: this module owns what each group offers and
 * how a snippet lands in the text. `FlashBookFormulaKeyboard` only draws it. Two
 * consequences worth keeping:
 *
 *  - Snippets are placeholders, not characters. `SLOT` marks where the caret
 *    belongs after the insert, which is what makes "∫‸d‸" a template the creator
 *    fills in rather than a string they have to repair.
 *  - **Structure is LaTeX, symbols are characters.** The block typesets its
 *    source (see `#/lib/flashbookTex`), so a fraction key has to insert
 *    `\frac{‸}{‸}` — not `(‸)⁄(‸)`, which was what a flat string needed and which
 *    is now just parentheses and a slash. Symbols stay as the characters
 *    themselves: `α` and `≤` are perfectly good LaTeX, they are what a creator
 *    recognises on a key, and the parser reads them.
 */

/** Where the caret lands after a snippet is inserted. Never a real character. */
export const FORMULA_SLOT = '\u2038'

export type FormulaKey = {
  /** What the key shows. */
  glyph: string
  /** What it inserts. Defaults to the glyph; `FORMULA_SLOT` marks the caret. */
  insert?: string
  /** How the key announces itself (screen readers, tooltips). */
  name: string
}

export type FormulaGroup = {
  id: string
  /** Tab label. Short: the tabs sit in a strip above the keys. */
  label: string
  keys: FormulaKey[]
}

/**
 * A script key: a glyph to show, and the LaTeX that raises or lowers it.
 *
 * The *label* is what a creator reads at a glance (`x²`), and the *insert* is
 * what the typesetter needs (`x^{2}`) — which is the whole point of the split: a
 * Unicode superscript would be printed as a small character sitting on the
 * baseline, not as an exponent.
 */
const script = (
  glyph: string,
  body: string,
  name: string,
  kind: 'sup' | 'sub' = 'sup',
): FormulaKey => ({
  glyph,
  insert: kind === 'sup' ? `^{${body}\u2038}` : `_{${body}\u2038}`,
  name,
})

/** A power of a base, shown as `x²` and written as an exponent. */
const powers = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  'n',
  'a',
  'k',
  'y',
].map(
  (body): FormulaKey => ({
    glyph: `x^${body === 'n' ? 'ⁿ' : body}`,
    insert: `^{${body}}\u2038`,
    name: `to the ${body}`,
  }),
)

/** And an index, shown the same way and written below the line. */
const indices = ['0', '1', '2', '3', 'n', 'i', 'j', 'k', 't', 'r'].map(
  (body): FormulaKey => ({
    glyph: `x_${body}`,
    insert: `_{${body}}\u2038`,
    name: `index ${body}`,
  }),
)

export const FORMULA_GROUPS: FormulaGroup[] = [
  {
    id: 'operators',
    label: 'Operators',
    keys: [
      {glyph: '+', name: 'plus'},
      {glyph: '−', name: 'minus'},
      {glyph: '×', name: 'times'},
      {glyph: '÷', name: 'divided by'},
      {glyph: '±', name: 'plus or minus'},
      {glyph: '∓', name: 'minus or plus'},
      {glyph: '·', name: 'dot product'},
      {glyph: '∗', name: 'asterisk operator'},
      {glyph: '=', name: 'equals'},
      {glyph: '≠', name: 'not equal'},
      {glyph: '≈', name: 'approximately'},
      {glyph: '≡', name: 'identical to'},
      {glyph: '<', name: 'less than'},
      {glyph: '>', name: 'greater than'},
      {glyph: '≤', name: 'less or equal'},
      {glyph: '≥', name: 'greater or equal'},
      {glyph: '≪', name: 'much less'},
      {glyph: '≫', name: 'much greater'},
      {glyph: '∝', name: 'proportional to'},
      {glyph: '∞', name: 'infinity'},
      {glyph: '%', name: 'percent'},
      {glyph: '‰', name: 'per mille'},
      /*
       * The fraction, in the group a creator looks for arithmetic in.
       *
       * A bare `⁄` used to sit here — the last trace of the flat-string model,
       * where a fraction slash was the closest a `Text` could come to a fraction.
       * Tapping it produced a slanted bar, which is not a fraction and does not
       * become one, so the block appeared to have no fraction key at all. It now
       * writes the same structure the Algebra group's key does: what a creator
       * gets is a stacked fraction, and "fraction" is arithmetic rather than
       * algebra, which is why the key belongs here too.
       */
      {glyph: 'a⁄b', insert: '\\frac{\u2038}{\u2038}', name: 'fraction'},
      {glyph: '√‸', insert: '\\sqrt{\u2038}', name: 'root'},
    ],
  },
  {
    id: 'powers',
    label: 'Powers',
    keys: [
      ...powers,
      // The exponents and indices that are spelled with a letter rather than a
      // digit, which is most of them in algebra and physics.
      script('x⁺', '+', 'power plus'),
      script('x⁻', '-', 'power minus'),
      ...indices,
      {glyph: '√', insert: '\\sqrt{\u2038}', name: 'square root'},
      {glyph: '∛', insert: '\\sqrt[3]{\u2038}', name: 'cube root'},
      {glyph: '∜', insert: '\\sqrt[4]{\u2038}', name: 'fourth root'},
      {glyph: 'ⁿ√', insert: '\\sqrt[\u2038]{\u2038}', name: 'nth root'},
    ],
  },
  {
    id: 'greek',
    label: 'Greek',
    keys: [
      {glyph: 'α', name: 'alpha'},
      {glyph: 'β', name: 'beta'},
      {glyph: 'γ', name: 'gamma'},
      {glyph: 'δ', name: 'delta'},
      {glyph: 'ε', name: 'epsilon'},
      {glyph: 'ζ', name: 'zeta'},
      {glyph: 'η', name: 'eta'},
      {glyph: 'θ', name: 'theta'},
      {glyph: 'ι', name: 'iota'},
      {glyph: 'κ', name: 'kappa'},
      {glyph: 'λ', name: 'lambda'},
      {glyph: 'μ', name: 'mu'},
      {glyph: 'ν', name: 'nu'},
      {glyph: 'ξ', name: 'xi'},
      {glyph: 'π', name: 'pi'},
      {glyph: 'ρ', name: 'rho'},
      {glyph: 'σ', name: 'sigma'},
      {glyph: 'τ', name: 'tau'},
      {glyph: 'υ', name: 'upsilon'},
      {glyph: 'φ', name: 'phi'},
      {glyph: 'χ', name: 'chi'},
      {glyph: 'ψ', name: 'psi'},
      {glyph: 'ω', name: 'omega'},
      {glyph: 'Γ', name: 'capital gamma'},
      {glyph: 'Δ', name: 'capital delta'},
      {glyph: 'Θ', name: 'capital theta'},
      {glyph: 'Λ', name: 'capital lambda'},
      {glyph: 'Ξ', name: 'capital xi'},
      {glyph: 'Π', name: 'capital pi'},
      {glyph: 'Σ', name: 'capital sigma'},
      {glyph: 'Φ', name: 'capital phi'},
      {glyph: 'Ψ', name: 'capital psi'},
      {glyph: 'Ω', name: 'capital omega'},
    ],
  },
  {
    id: 'algebra',
    label: 'Algebra',
    keys: [
      /*
       * The structure keys, which are what the block could not express before:
       * a real fraction, an overline, a vector, a bracketed phrase of prose. Each
       * is one tap, and each leaves the caret where the writing goes next rather
       * than at the end of a string to repair.
       */
      {glyph: 'a⁄b', insert: '\\frac{\u2038}{\u2038}', name: 'fraction'},
      {glyph: '‸²', insert: '^{\u2038}', name: 'power'},
      {glyph: '‸ₙ', insert: '_{\u2038}', name: 'index'},
      {glyph: '√‸', insert: '\\sqrt{\u2038}', name: 'root'},
      {glyph: 'ā', insert: '\\overline{\u2038}', name: 'overbar'},
      {glyph: 'a⃗', insert: '\\vec{\u2038}', name: 'vector'},
      {glyph: 'abc', insert: '\\text{\u2038}', name: 'upright text'},
      {glyph: '↵', insert: '\\\\\u2038', name: 'new line'},
      {glyph: '( )', insert: '(\u2038)', name: 'parentheses'},
      {glyph: '[ ]', insert: '[\u2038]', name: 'square brackets'},
      {glyph: '{ }', insert: '{\u2038}', name: 'braces'},
      {glyph: '| |', insert: '|\u2038|', name: 'absolute value'},
      {glyph: '⌈ ⌉', insert: '⌈\u2038⌉', name: 'ceiling'},
      {glyph: '⌊ ⌋', insert: '⌊\u2038⌋', name: 'floor'},
      {glyph: '⟨ ⟩', insert: '⟨\u2038⟩', name: 'angle brackets'},
      {glyph: 'n!', insert: '\u2038!', name: 'factorial'},
      {glyph: 'x′', insert: '\u2038′', name: 'prime'},
      {glyph: 'x″', insert: '\u2038″', name: 'double prime'},
      {glyph: '·', name: 'middle dot'},
      {glyph: '×', name: 'times'},
      {glyph: '⋮', name: 'vertical ellipsis'},
      {glyph: '⋯', name: 'ellipsis'},
      {glyph: '⋯⋯', insert: '⋯‸', name: 'continues'},
      {glyph: '∅', name: 'empty set'},
    ],
  },
  {
    id: 'calculus',
    label: 'Calculus',
    keys: [
      {glyph: '∫', name: 'integral'},
      {
        glyph: '∫ₐᵇ',
        insert: '\\int_{\u2038}^{\u2038}',
        name: 'definite integral',
      },
      {
        glyph: '∫ dx',
        insert: '\\int \u2038d\u2038',
        name: 'integral with respect to',
      },
      {glyph: '∬', name: 'double integral'},
      {glyph: '∭', name: 'triple integral'},
      {glyph: '∮', name: 'contour integral'},
      {glyph: '∂', name: 'partial'},
      /*
       * A derivative is a fraction, and now it can be one: `\frac{d}{dx}` is what
       * a textbook prints, and the caret starts in the place the creator has to
       * fill in for themselves — the variable.
       */
      {
        glyph: '∂/∂x',
        insert: '\\frac{\\partial \u2038}{\\partial x}',
        name: 'partial derivative',
      },
      {glyph: 'd/dx', insert: '\\frac{d\u2038}{dx}', name: 'derivative'},
      {
        glyph: 'd²/dx²',
        insert: '\\frac{d^{2}\u2038}{dx^{2}}',
        name: 'second derivative',
      },
      {glyph: '∇', name: 'nabla'},
      {glyph: '∆', name: 'delta'},
      {glyph: '∑', name: 'sum'},
      {glyph: '∑ᵢ', insert: '\\sum_{\u2038}', name: 'sum with index'},
      {glyph: '∏', name: 'product'},
      {glyph: 'lim', insert: '\\lim_{\u2038 \\to \u2038}', name: 'limit'},
      {glyph: '→', name: 'tends to'},
      {glyph: '→∞', insert: '\u2038→∞', name: 'tends to infinity'},
    ],
  },
  {
    id: 'geometry',
    label: 'Geometry',
    keys: [
      {glyph: '°', name: 'degrees'},
      {glyph: '′', name: 'minutes'},
      {glyph: '″', name: 'seconds'},
      {glyph: '∠', name: 'angle'},
      {glyph: '∠ABC', insert: '∠\u2038', name: 'named angle'},
      {glyph: '⊥', name: 'perpendicular'},
      {glyph: '∥', name: 'parallel'},
      {glyph: '∦', name: 'not parallel'},
      {glyph: '△', name: 'triangle'},
      {glyph: '△ABC', insert: '△\u2038', name: 'named triangle'},
      {glyph: '▲', name: 'filled triangle'},
      {glyph: '□', name: 'square'},
      {glyph: '▭', name: 'rectangle'},
      {glyph: '▱', name: 'parallelogram'},
      {glyph: '○', name: 'circle'},
      {glyph: '●', name: 'filled circle'},
      {glyph: '⊙', name: 'circle with centre'},
      {glyph: '⌒', name: 'arc'},
      {glyph: '◠', name: 'upper arc'},
      {glyph: '◡', name: 'lower arc'},
      {glyph: '◑', name: 'half circle'},
      {glyph: '◇', name: 'diamond'},
      {glyph: '◆', name: 'filled diamond'},
      {glyph: '⬡', name: 'hexagon'},
      {glyph: '★', name: 'star'},
      {glyph: '≅', name: 'congruent'},
      {glyph: '∼', name: 'similar'},
      {glyph: '⟂', name: 'right angle'},
      {glyph: '⌀', name: 'diameter'},
      {glyph: 'r²', insert: 'r^{2}', name: 'squared radius'},
      {glyph: 'π r²', insert: '\\pi r^{2}', name: 'area of circle'},
      {glyph: '½bh', insert: '\\frac{1}{2}bh', name: 'area of triangle'},
    ],
  },
  {
    id: 'physics',
    label: 'Physics',
    keys: [
      {glyph: 'ħ', name: 'reduced planck constant'},
      {glyph: 'ℏ', name: 'planck constant over 2 pi'},
      {glyph: 'λ', name: 'wavelength'},
      {glyph: 'ν', name: 'frequency'},
      {glyph: 'ω', name: 'angular frequency'},
      {glyph: 'Ω', name: 'ohm'},
      {glyph: 'μ', name: 'micro'},
      {glyph: 'μ₀', name: 'vacuum permeability'},
      {glyph: 'ε', name: 'permittivity'},
      {glyph: 'ε₀', name: 'vacuum permittivity'},
      {glyph: 'σ', name: 'sigma'},
      {glyph: 'ρ', name: 'density'},
      {glyph: 'θ', name: 'angle theta'},
      {glyph: 'φ', name: 'flux'},
      {glyph: 'τ', name: 'torque'},
      {glyph: '∆', name: 'change in'},
      {glyph: 'Å', name: 'angstrom'},
      {glyph: 'ı̂', name: 'unit vector i'},
      {glyph: 'ĵ', name: 'unit vector j'},
      {glyph: 'k̂', name: 'unit vector k'},
      {glyph: '⟨ψ|', insert: '⟨\u2038|', name: 'bra'},
      {glyph: '|ψ⟩', insert: '|\u2038⟩', name: 'ket'},
      {glyph: '→', name: 'vector direction'},
      {glyph: '←', name: 'leftwards'},
      {glyph: '↑', name: 'upwards'},
      {glyph: '↓', name: 'downwards'},
      {glyph: '↔', name: 'both ways'},
      {glyph: '⇌', name: 'equilibrium arrow'},
      {glyph: '⇒', name: 'implies'},
      {glyph: '⇐', name: 'implied by'},
      {glyph: '⊕', name: 'direct sum'},
      {glyph: '⊗', name: 'tensor product'},
      {glyph: '·', name: 'scalar product'},
      {glyph: '∝', name: 'proportional to'},
    ],
  },
  {
    id: 'logic',
    label: 'Sets & logic',
    keys: [
      {glyph: '∈', name: 'element of'},
      {glyph: '∉', name: 'not element of'},
      {glyph: '∋', name: 'contains'},
      {glyph: '⊂', name: 'subset'},
      {glyph: '⊄', name: 'not subset'},
      {glyph: '⊆', name: 'subset or equal'},
      {glyph: '⊇', name: 'superset'},
      {glyph: '∪', name: 'union'},
      {glyph: '∩', name: 'intersection'},
      {glyph: '\\', name: 'set difference'},
      {glyph: '∅', name: 'empty set'},
      {glyph: '∀', name: 'for all'},
      {glyph: '∃', name: 'there exists'},
      {glyph: '∄', name: 'does not exist'},
      {glyph: '¬', name: 'not'},
      {glyph: '∧', name: 'and'},
      {glyph: '∨', name: 'or'},
      {glyph: '⇔', name: 'if and only if'},
      {glyph: '∴', name: 'therefore'},
      {glyph: '∵', name: 'because'},
      {glyph: '⊨', name: 'models'},
      {glyph: 'ℕ', name: 'natural numbers'},
      {glyph: 'ℤ', name: 'integers'},
      {glyph: 'ℚ', name: 'rationals'},
      {glyph: 'ℝ', name: 'reals'},
      {glyph: 'ℂ', name: 'complex numbers'},
    ],
  },
]

export type Caret = {start: number; end: number}

/**
 * Insert a snippet at the caret, replacing the selection.
 *
 * The snippet's first `FORMULA_SLOT` becomes the new caret position; any further
 * slots are simply dropped, so `(‸)⁄(‸)` is a two-slot fraction the creator fills
 * left to right rather than an unusable string of markers.
 */
export function insertFormulaSnippet(
  text: string,
  selection: Caret,
  snippet: string,
): {text: string; selection: Caret} {
  const length = text.length
  const raw = [selection.start, selection.end].map(n =>
    Math.max(0, Math.min(n, length)),
  )
  // Selections arrive reversed from some platforms when a selection was made
  // right-to-left, and a stale selection can sit outside the text after a
  // change. Both mean the same range, so normalise rather than guess.
  const start = Math.min(...raw)
  const end = Math.max(...raw)

  const slot = snippet.indexOf(FORMULA_SLOT)
  const literal = snippet.split(FORMULA_SLOT).join('')
  const next = text.slice(0, start) + literal + text.slice(end)

  const caret =
    slot < 0
      ? start + literal.length
      : start + snippet.slice(0, slot).split(FORMULA_SLOT).join('').length

  return {text: next, selection: {start: caret, end: caret}}
}

/** Delete one character before the caret — or the selection, if there is one. */
export function deleteBeforeCaret(
  text: string,
  selection: Caret,
): {text: string; selection: Caret} {
  const {start, end} = selection
  if (end > start) {
    return {
      text: text.slice(0, start) + text.slice(end),
      selection: {start, end: start},
    }
  }
  if (start === 0) return {text, selection}
  const next = text.slice(0, start - 1) + text.slice(start)
  return {text: next, selection: {start: start - 1, end: start - 1}}
}

/** Everything the keyboard can insert, for the palette's own search. */
export function findFormulaKeys(query: string): FormulaKey[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  return FORMULA_GROUPS.flatMap(group =>
    group.keys.filter(
      key =>
        key.name.toLowerCase().includes(q) ||
        key.glyph.toLowerCase() === q ||
        (key.insert ?? '').toLowerCase() === q,
    ),
  ).slice(0, 24)
}
